import { ArrowLeft, ArrowRight, Check } from "lucide-react"
import { useMemo, useState } from "react"
import type { TipoRubro } from "./PanelRubros"
import { formatearImporte, totalDelRubro } from "./PanelRubros"

/**
 * Wizard de cotización en tres pasos: cliente, ítems, revisión.
 *
 * ⚠ **El total del último paso se calcula y no se teclea.** No hay campo de total, y
 * ésa es la decisión de este componente: un total editable a mano puede decir una
 * cifra distinta de la suma de sus líneas, y entonces la cotización aprobada y el
 * expediente que nace de ella arrancan con dos verdades. Es la misma familia de fallo
 * que el `estado` y el `monto_cobrado` independientes del 2.0, con otra ropa. Por eso
 * el total se pinta en un `<output>` y no en un `<input>`.
 *
 * ⚠ Cada línea lleva **identidad propia**, no su índice. Con clave por índice,
 * borrar o reordenar una línea hace que React reutilice el nodo equivocado y el texto
 * que alguien acaba de teclear aparece en otra fila.
 */

type Paso = 1 | 2 | 3

export type LineaCotizacion = {
  id: string
  tipo: TipoRubro
  concepto: string
  cantidad: string
  precioUnitario: string
}

type Props = {
  clientes: { id: number; razonSocial: string }[]
  /**
   * ⚠ La aduana es **obligatoria**, no un adorno del paso 1. `CotizacionCrear` la
   * exige, y un wizard que no la pidiera terminaría siempre en un 422 al confirmar,
   * después de que alguien tecleara todas las líneas.
   */
  aduanas: { id: number; nombre: string }[]
  onConfirmar: (datos: { clienteId: number; aduanaId: number; lineas: LineaCotizacion[] }) => void
}

/**
 * Suma las líneas. **La única fuente del total.**
 *
 * Se exporta para poder probarla, pero el componente no ofrece ninguna forma de
 * sobrescribir su resultado: no existe un `<input>` de total.
 */
export function totalDeLaCotizacion(lineas: LineaCotizacion[]): string {
  const total = lineas.reduce((suma, linea) => suma + Number(totalDelRubro(linea)), 0)
  return total.toFixed(2)
}

function lineaNueva(): LineaCotizacion {
  return {
    id: crypto.randomUUID(),
    tipo: "SERVICIO",
    concepto: "",
    cantidad: "1",
    precioUnitario: "0.00",
  }
}

export function WizardCotizacion({ clientes, aduanas, onConfirmar }: Props) {
  const [paso, setPaso] = useState<Paso>(1)
  const [clienteId, setClienteId] = useState<number | null>(null)
  const [aduanaId, setAduanaId] = useState<number | null>(null)
  const [lineas, setLineas] = useState<LineaCotizacion[]>([])

  const total = useMemo(() => totalDeLaCotizacion(lineas), [lineas])
  // El paso 1 no se deja atrás sin los dos datos que el servidor exige. Descubrirlo
  // en el paso 3 sería descubrirlo después de teclear todas las líneas.
  const puedeAvanzar = paso === 1 ? clienteId !== null && aduanaId !== null : lineas.length > 0
  const borde = { borderColor: "var(--border)" }

  return (
    <section className="space-y-4" aria-label="Nueva cotización">
      <ol className="flex gap-2 text-sm" aria-label="Pasos">
        {(["Cliente", "Ítems", "Revisión"] as const).map((nombre, i) => (
          <li
            key={nombre}
            aria-current={paso === i + 1 ? "step" : undefined}
            className="rounded-[var(--radius-control)] px-3 py-1"
            style={
              paso === i + 1
                ? { background: "var(--primary)", color: "var(--primary-fg)" }
                : { background: "var(--surface-elevated)", color: "var(--fg-muted)" }
            }
          >
            {i + 1}. {nombre}
          </li>
        ))}
      </ol>

      {paso === 1 && (
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm">Cliente</span>
            <select
              className="campo mt-1"
              value={clienteId ?? ""}
              onChange={(e) => setClienteId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Selecciona un cliente</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razonSocial}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm">Aduana</span>
            <select
              className="campo mt-1"
              value={aduanaId ?? ""}
              onChange={(e) => setAduanaId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Selecciona una aduana</option>
              {aduanas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nombre}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {paso === 2 && <LineasEditables lineas={lineas} onCambiar={setLineas} />}

      {paso === 3 && (
        <div className="superficie p-4">
          <h3 className="font-medium">Revisión</h3>
          <ul className="mt-2 space-y-1">
            {lineas.map((linea) => (
              <li key={linea.id} className="flex justify-between">
                <span>{linea.concepto}</span>
                <span className="tabular-nums">
                  {formatearImporte(totalDelRubro(linea), "USD", "")}
                </span>
              </li>
            ))}
          </ul>
          {/* ⚠ Un `<output>`, no un `<input>`: el total no se teclea. */}
          <p className="mt-3 flex justify-between border-t pt-3 font-semibold" style={borde}>
            <span>Total</span>
            <output data-testid="total-cotizacion" className="tabular-nums">
              {formatearImporte(total, "USD", "")}
            </output>
          </p>
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          disabled={paso === 1}
          onClick={() => setPaso((p) => (p > 1 ? ((p - 1) as Paso) : p))}
          className="flex items-center gap-1 rounded-[var(--radius-control)] border px-3 py-1.5 disabled:opacity-40"
          style={borde}
        >
          <ArrowLeft size={16} aria-hidden={true} /> Atrás
        </button>
        {paso < 3 ? (
          <button
            type="button"
            disabled={!puedeAvanzar}
            onClick={() => setPaso((p) => (p + 1) as Paso)}
            className="flex items-center gap-1 rounded-[var(--radius-control)] px-3 py-1.5 disabled:opacity-40"
            style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
          >
            Siguiente <ArrowRight size={16} aria-hidden={true} />
          </button>
        ) : (
          <button
            type="button"
            onClick={() =>
              clienteId !== null &&
              aduanaId !== null &&
              onConfirmar({ clienteId, aduanaId, lineas })
            }
            className="flex items-center gap-1 rounded-[var(--radius-control)] px-3 py-1.5"
            style={{ background: "var(--success)", color: "var(--primary-fg)" }}
          >
            <Check size={16} aria-hidden={true} /> Crear cotización
          </button>
        )}
      </div>
    </section>
  )
}

function LineasEditables({
  lineas,
  onCambiar,
}: {
  lineas: LineaCotizacion[]
  onCambiar: (l: LineaCotizacion[]) => void
}) {
  const borde = { borderColor: "var(--border)" }
  const cambiar = (id: string, campo: keyof LineaCotizacion, valor: string) =>
    onCambiar(lineas.map((l) => (l.id === id ? { ...l, [campo]: valor } : l)))

  return (
    <div className="space-y-2">
      {lineas.map((linea, i) => (
        <div key={linea.id} className="flex gap-2">
          <input
            aria-label={`Concepto de la línea ${i + 1}`}
            className="campo flex-1"
            value={linea.concepto}
            onChange={(e) => cambiar(linea.id, "concepto", e.target.value)}
          />
          <input
            aria-label={`Cantidad de la línea ${i + 1}`}
            className="campo w-24 text-right tabular-nums"
            value={linea.cantidad}
            onChange={(e) => cambiar(linea.id, "cantidad", e.target.value)}
          />
          <input
            aria-label={`Precio de la línea ${i + 1}`}
            className="campo w-28 text-right tabular-nums"
            value={linea.precioUnitario}
            onChange={(e) => cambiar(linea.id, "precioUnitario", e.target.value)}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={() => onCambiar([...lineas, lineaNueva()])}
        className="rounded-[var(--radius-control)] border border-dashed px-3 py-1.5 text-sm"
        style={{ ...borde, color: "var(--fg-muted)" }}
      >
        Añadir línea
      </button>
    </div>
  )
}
